const LOCAL_DEV_BACKEND_FALLBACK = 'http://localhost:3001';
export const API_BASE_SENTINEL = 'affinityai://missing-backend-url';

function stripTrailingSlash(s) {
  return s.replace(/\/+$/, '');
}

export function isLocalHost(host) {
  const normalizedHost = String(host || '')
    .trim()
    .toLowerCase();
  if (!normalizedHost) return false;

  // location.hostname omits ports, but keep this tolerant for callers/tests.
  const hostWithoutPort = normalizedHost.replace(/:\d+$/, '');
  return (
    hostWithoutPort === 'localhost' ||
    hostWithoutPort === '127.0.0.1' ||
    hostWithoutPort.endsWith('.localhost')
  );
}

export function normalizeBackendBase(raw) {
  const s = (raw || '').trim();
  if (!s) return '';

  if (/^https?:\/\//i.test(s)) return stripTrailingSlash(s);

  if (/^(localhost|127\.0\.0\.1)(:\d+)?(\/.*)?$/i.test(s)) {
    return stripTrailingSlash(`http://${s}`);
  }

  return stripTrailingSlash(`https://${s}`);
}

export function isValidAbsoluteUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function readConfiguredBackendValue(env) {
  const fromWindow =
    (typeof window !== 'undefined' && window.__AFFINITYAI_CONFIG__?.BACKEND_URL) || '';
  const runtimeValue = String(fromWindow).trim();
  if (runtimeValue) return { source: 'runtime-config', value: runtimeValue };

  const safeEnv = env || {};

  const fromCRA = (safeEnv.REACT_APP_BACKEND_URL || '').trim();
  if (fromCRA) return { source: 'REACT_APP_BACKEND_URL', value: fromCRA };

  const fromNext = (safeEnv.NEXT_PUBLIC_BACKEND_URL || '').trim();
  if (fromNext) return { source: 'NEXT_PUBLIC_BACKEND_URL', value: fromNext };

  return { source: '', value: '' };
}

export function detectBackendEnvironment({ env = {}, hostname = '' } = {}) {
  const nodeEnv = String(env.NODE_ENV || '').toLowerCase();
  const vercelEnv = String(
    env.REACT_APP_VERCEL_ENV || env.NEXT_PUBLIC_VERCEL_ENV || env.VERCEL_ENV || '',
  ).toLowerCase();
  const host = String(hostname || '').toLowerCase();
  const isHostLocal = isLocalHost(host);

  if (vercelEnv === 'preview') return 'preview';
  if (vercelEnv === 'production') return 'production';
  if (vercelEnv === 'development') return 'development';
  if (nodeEnv === 'development' && isHostLocal) return 'development';
  if (nodeEnv === 'development') {
    if (host.endsWith('.vercel.app')) return 'preview';
    return 'production';
  }
  if (nodeEnv === 'test') return 'test';

  // When VERCEL_ENV is not exposed to the client bundle, .vercel.app hosts are
  // typically preview deployments unless explicitly marked as production.
  if (nodeEnv === 'production' && host.endsWith('.vercel.app')) return 'preview';
  if (nodeEnv === 'production') return 'production';
  return 'unknown';
}

export function isStrictBackendEnv(runtimeEnv) {
  return runtimeEnv === 'preview' || runtimeEnv === 'production';
}

export function resolveBackendConfig({ env = {}, hostname = '' } = {}) {
  const runtimeEnv = detectBackendEnvironment({ env, hostname });
  const hostIsLocal = isLocalHost(hostname);
  const hostLabel = String(hostname || '').trim() || 'unknown host';
  const configured = readConfiguredBackendValue(env);
  const normalized = normalizeBackendBase(configured.value);

  if (configured.value) {
    if (isValidAbsoluteUrl(normalized)) {
      return {
        runtimeEnv,
        source: configured.source,
        baseUrl: normalized,
        isSentinel: false,
        requiresAttention: false,
        message: '',
      };
    }

    const message = `[AffinityAI] Invalid ${configured.source} value "${configured.value}".`;
    if (!hostIsLocal) {
      return {
        runtimeEnv,
        source: configured.source,
        baseUrl: API_BASE_SENTINEL,
        isSentinel: true,
        requiresAttention: true,
        message: `${message} Refusing localhost fallback on non-local host (${hostLabel}).`,
      };
    }

    return {
      runtimeEnv,
      source: configured.source,
      baseUrl: LOCAL_DEV_BACKEND_FALLBACK,
      isSentinel: false,
      requiresAttention: true,
      message: `${message} Falling back to ${LOCAL_DEV_BACKEND_FALLBACK} on local host.`,
    };
  }

  if (!hostIsLocal) {
    return {
      runtimeEnv,
      source: '',
      baseUrl: API_BASE_SENTINEL,
      isSentinel: true,
      requiresAttention: true,
      message:
        `[AffinityAI] Missing backend URL on non-local host (${hostLabel}). ` +
        'Set REACT_APP_BACKEND_URL or NEXT_PUBLIC_BACKEND_URL.',
    };
  }

  return {
    runtimeEnv,
    source: '',
    baseUrl: LOCAL_DEV_BACKEND_FALLBACK,
    isSentinel: false,
    requiresAttention: false,
    message: '',
  };
}
